import { useState, useEffect, useRef } from 'react';
import { Check, Clock, Search, X } from 'lucide-react';
import { findOrdersForCode } from '../scanner.jsx';
import { BrowserMultiFormatReader } from '@zxing/browser';

// ── Scanner screen ────────────────────────────────────────────────────────────
export function Scanner({ store, currentRole, dark }) {
  const [manualInput, setManualInput] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [sentFeedback, setSentFeedback] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  // Always-current ref so the ZXing callback (captured once at mount) reads
  // the latest orders even after new orders have been added.
  const ordersRef = useRef(store.orders ?? []);
  useEffect(() => { ordersRef.current = store.orders ?? []; }, [store.orders]);

  // ── Inline camera via ZXing ───────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    reader.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
      if (!active) return;
      if (result) {
        const code = result.getText();
        const matches = findOrdersForCode(code, ordersRef.current);
        if (matches.length > 0) {
          store.setLastMatchedOrder(matches[0]);
          store.setLastUnmatchedCode(null);
        } else {
          store.setLastMatchedOrder(null);
          store.setLastUnmatchedCode(code);
        }
      }
    }).catch((e) => {
      if (active) setCameraError(e?.message || 'Kamera nicht verfügbar');
    });

    return () => {
      active = false;
      BrowserMultiFormatReader.releaseAllStreams();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Manual entry ──────────────────────────────────────────────────────────
  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualInput.trim()) return;
    const code = manualInput.trim();
    const matches = findOrdersForCode(code, store.orders ?? []);
    if (matches.length > 0) {
      store.setLastMatchedOrder(matches[0]);
      store.setLastUnmatchedCode(null);
    } else {
      store.setLastMatchedOrder(null);
      store.setLastUnmatchedCode(code);
    }
    setManualInput('');
    setSentFeedback(false);
  };

  // ── Mark as shipped ───────────────────────────────────────────────────────
  const handleMarkSent = () => {
    const order = store.lastMatchedOrder;
    if (!order) return;
    store.updateOrder(order.id, 'status', 'Versendet');
    setScanHistory((prev) => [
      {
        id: order.bestellnummer || order.id,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        status: 'Versendet',
      },
      ...prev,
    ]);
    setSentFeedback(true);
    // Reset feedback after a moment
    setTimeout(() => setSentFeedback(false), 2000);
  };

  const order = store.lastMatchedOrder;
  const unmatchedCode = store.lastUnmatchedCode;
  const canMarkSent =
    order && order.status !== 'Versendet' && order.status !== 'Retoure';

  // Derive CSS status class
  const statusClass = (status) => {
    const s = (status ?? '').toLowerCase();
    if (s === 'versendet') return 'status-versendet';
    if (s === 'bereit') return 'status-bereit';
    if (s === 'retoure') return 'status-retoure';
    return 'status-bereit';
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto px-5 pt-4" style={{ paddingBottom: 112 }}>
      {/* ── Viewfinder (inline camera) ────────────────────────────────────── */}
      <div className="px-4 pt-4">
        <div className="scanner-viewfinder relative mx-auto aspect-[4/3] w-full max-w-sm overflow-hidden">
          {/* Corner guides */}
          <div className="scanner-corner top-left" />
          <div className="scanner-corner top-right" />
          <div className="scanner-corner bottom-left" />
          <div className="scanner-corner bottom-right" />

          {/* Scanning line animation */}
          <div className="scanner-line" />

          {/* Live camera feed */}
          <video
            ref={videoRef}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit',
            }}
            muted
            playsInline
          />

          {/* Camera error overlay */}
          {cameraError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
              <p className="text-white/80 text-xs text-center px-4">{cameraError}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Manual input ─────────────────────────────────────────────────── */}
      <form onSubmit={handleManualSubmit} className="mt-4">
        <div className="glass-card flex items-center gap-3 rounded-xl px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            type="text"
            placeholder="Manuell eingeben"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {manualInput && (
            <button
              type="button"
              onClick={() => setManualInput('')}
              className="text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="submit"
            className="rounded-lg bg-ios-blue px-4 py-2 text-sm font-medium text-white flex-shrink-0 whitespace-nowrap"
          >
            Suchen
          </button>
        </div>
      </form>

      {/* ── No-match notice ───────────────────────────────────────────────── */}
      {!order && unmatchedCode && (
        <div className="mt-4 mx-4">
          <div className="glass-card rounded-2xl p-4 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
              <X className="h-4 w-4 text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium">Keine Bestellung gefunden</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Code: <span className="font-mono">{unmatchedCode}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Last matched order card ───────────────────────────────────────── */}
      {order && (
        <div className="mt-4 mx-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Zuletzt gescannt
          </h3>
          <div className="glass-card glass-shine glass-card-online rounded-2xl p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="font-semibold">
                  #{order.bestellnummer || order.id}
                </span>
                {(order.channelId || order.kanal) && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {store.getChannelLabel?.(order.channelId) || order.kanal}
                  </p>
                )}
              </div>
              <span className={`status-pill ${statusClass(order.status)}`}>
                {order.status ?? 'Unbekannt'}
              </span>
            </div>

            {order.kunde && (
              <p className="text-sm text-muted-foreground mb-3">{order.kunde}</p>
            )}

            {sentFeedback ? (
              <div className="w-full rounded-xl bg-ios-green/20 py-3 flex items-center justify-center gap-2 text-sm font-medium text-ios-green">
                <Check className="h-4 w-4" />
                Als versendet markiert
              </div>
            ) : canMarkSent ? (
              <button
                onClick={handleMarkSent}
                className="w-full rounded-xl bg-ios-green py-3 font-medium text-white transition-all active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-2">
                  <Check className="h-5 w-5" />
                  Als versendet markieren
                </span>
              </button>
            ) : (
              <div className="w-full rounded-xl bg-muted/40 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4" />
                {order.status === 'Versendet' ? 'Bereits versendet' : 'Status: ' + order.status}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Scan history chips ────────────────────────────────────────────── */}
      {scanHistory.length > 0 && (
        <div className="mt-6 mx-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Letzte Scans dieser Sitzung
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollable-x">
            {scanHistory.map((scan, i) => (
              <div
                key={`${scan.id}-${i}`}
                className="glass-card flex-shrink-0 rounded-xl px-4 py-3 min-w-[120px]"
              >
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-ios-green" />
                  <span className="font-medium text-sm">#{scan.id}</span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {scan.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
